import React, { useState, useEffect } from 'react';
import './ImageDetail.css';
import AddTextModal from './AddTextModal';
import ChangeCategoryModal from './ChangeCategoryModal';
import { updateImageByFilename } from '../firebase/dataService';

const formatHijriDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('ar-SA-u-ca-islamic', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch {
    return null;
  }
};

function ImageDetail({ image, images, currentIndex, onBack, onImageUpdated, onNavigate }) {
  const [showAddTextModal, setShowAddTextModal] = useState(false);
  const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(image);

  // التمرير الفوري إلى الأعلى عند تحميل المكون
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    setCurrentImage(image);
    // التمرير إلى أعلى الصفحة عند تغيير الصورة
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [image]);

  const handlePrevious = () => {
    if (images && currentIndex > 0 && onNavigate) {
      onNavigate(images[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (images && currentIndex < images.length - 1 && onNavigate) {
      onNavigate(images[currentIndex + 1]);
    }
  };

  const hasPrevious = images && currentIndex > 0;
  const hasNext = images && currentIndex < images.length - 1;
  const showNavigation = images && images.length > 1;
  const handleSaveText = async (newText) => {
    try {
      // تحديث محلي
      const updatedImage = { 
        ...currentImage, 
        extractedText: newText
      };
      setCurrentImage(updatedImage);
      
      if (onImageUpdated) {
        onImageUpdated(updatedImage);
      }
      
      // محاولة التحديث في Firebase (إذا كان متاحاً)
      try {
        if (currentImage.filename) {
          await updateImageByFilename(currentImage.filename, { extractedText: newText });
          console.log('✅ تم حفظ التفريغ النصي في Firebase');
        }
      } catch (firebaseError) {
        console.warn('Firebase update failed, changes saved locally only:', firebaseError);
      }
      
      const successMsg = document.createElement('div');
      successMsg.className = 'delete-success-message';
      successMsg.textContent = '✅ تم حفظ التفريغ النصي بنجاح';
      document.body.appendChild(successMsg);
      
      setTimeout(() => {
        successMsg.remove();
      }, 3000);
    } catch (error) {
      console.error('Error saving text:', error);
      throw error;
    }
  };

  const handleSaveCategory = async (newCategory) => {
    try {
      // تحديث محلي
      const updatedImage = { 
        ...currentImage, 
        category: newCategory 
      };
      setCurrentImage(updatedImage);
      
      if (onImageUpdated) {
        onImageUpdated(updatedImage);
      }
      
      // محاولة التحديث في Firebase (إذا كان متاحاً)
      try {
        if (currentImage.filename) {
          await updateImageByFilename(currentImage.filename, { category: newCategory });
          console.log('✅ تم حفظ التصنيف في Firebase');
        }
      } catch (firebaseError) {
        console.warn('Firebase update failed, changes saved locally only:', firebaseError);
      }
      
      const successMsg = document.createElement('div');
      successMsg.className = 'delete-success-message';
      successMsg.textContent = '✅ تم تغيير التصنيف بنجاح';
      document.body.appendChild(successMsg);
      
      setTimeout(() => {
        successMsg.remove();
      }, 3000);
    } catch (error) {
      console.error('Error saving category:', error);
      throw error;
    }
  };

  const shareOnWhatsApp = () => {
    if (!currentImage.extractedText || currentImage.extractedText.trim() === '') {
      showNotification('⚠️ لا يوجد تفريغ نصي لهذا التصميم', 'error');
      return;
    }
    
    const text = `${currentImage.caption}\n\n${currentImage.extractedText}`;
    const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(text)}`;
    
    // محاولة فتح تطبيق الواتساب مباشرة
    window.location.href = whatsappUrl;
    
    // Fallback للويب إذا لم يفتح التطبيق
    setTimeout(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }, 1000);
  };

  const shareOnTwitter = () => {
    if (!currentImage.extractedText || currentImage.extractedText.trim() === '') {
      showNotification('⚠️ لا يوجد تفريغ نصي لهذا التصميم', 'error');
      return;
    }
    
    const text = `${currentImage.caption}\n\n${currentImage.extractedText}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareOnTelegram = () => {
    if (!currentImage.extractedText || currentImage.extractedText.trim() === '') {
      showNotification('⚠️ لا يوجد تفريغ نصي لهذا التصميم', 'error');
      return;
    }
    
    const text = `${currentImage.caption}\n\n${currentImage.extractedText}`;
    const telegramUrl = `tg://msg?text=${encodeURIComponent(text)}`;
    
    // محاولة فتح تطبيق تلجرام مباشرة
    window.location.href = telegramUrl;
    
    // Fallback للويب إذا لم يفتح التطبيق
    setTimeout(() => {
      window.open(`https://t.me/share/url?text=${encodeURIComponent(text)}`, '_blank');
    }, 1000);
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const file = new File([blob], `${currentImage.caption}.jpg`, { type: 'image/jpeg' });
      
      // محاولة استخدام Web Share API للحفظ في المعرض
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: currentImage.caption,
          text: 'حفظ الصورة'
        });
        showNotification('✓ تم حفظ الصورة', 'success');
        return;
      }
      
      // إذا لم يدعم المتصفح Web Share API، استخدم التحميل المباشر
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentImage.caption}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('✓ تم حفظ الصورة', 'success');
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: Direct download
      try {
        const link = document.createElement('a');
        link.href = currentImage.url;
        link.download = `${currentImage.caption}.jpg`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showNotification('✓ تم بدء التحميل', 'success');
      } catch (fallbackError) {
        showNotification('⚠️ حدث خطأ في الحفظ', 'error');
      }
    }
  };

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      showNotification('تم نسخ الرابط بنجاح ✓', 'success');
    }).catch(() => {
      showNotification('عذراً، حدث خطأ أثناء نسخ الرابط', 'error');
    });
  };

  const copyText = () => {
    const textToCopy = `${currentImage.caption}\n\n${currentImage.extractedText || 'تصميم من سلسلة الفوائد'}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      showNotification('تم نسخ النص بنجاح ✓', 'success');
    }).catch(err => {
      console.error('Error copying text:', err);
      showNotification('عذراً، حدث خطأ أثناء نسخ النص', 'error');
    });
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.className = `custom-notification ${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 2500);
  };

  // حساب السنة الهجرية الحالية
  const getHijriYear = () => {
    const today = new Date();
    const gregorianYear = today.getFullYear();
    const gregorianMonth = today.getMonth() + 1;
    
    // حساب تقريبي للسنة الهجرية
    // الفرق بين التقويمين حوالي 578 سنة
    let hijriYear = gregorianYear - 578;
    
    // تعديل بسيط حسب الشهر (لأن السنة الهجرية تبدأ في أوقات مختلفة)
    // السنة الهجرية 1447 بدأت في يوليو 2025
    if (gregorianMonth < 7) {
      hijriYear -= 1;
    }
    
    return hijriYear;
  };

  return (
    <>
      <AddTextModal
        isOpen={showAddTextModal}
        onClose={() => setShowAddTextModal(false)}
        onSave={handleSaveText}
        currentText={currentImage.extractedText || ''}
        imageName={currentImage.caption}
      />
      
      <ChangeCategoryModal
        isOpen={showChangeCategoryModal}
        onClose={() => setShowChangeCategoryModal(false)}
        onSave={handleSaveCategory}
        currentCategory={currentImage.category || 'متنوعة'}
        imageName={currentImage.caption}
      />
      
      <div className="image-detail">
        <div className="detail-container">
          <div className="navigation-header">
            <button className="back-button" onClick={onBack}>
              ← العودة للموقع
            </button>
            {showNavigation && (
              <div className="navigation-buttons">
                <button 
                  className="nav-btn prev-btn" 
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  title="التصميم السابق"
                >
                  → السابق
                </button>
                <span className="image-counter">
                  {currentIndex + 1} / {images.length}
                </span>
                <button 
                  className="nav-btn next-btn" 
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="التصميم التالي"
                >
                  التالي ←
                </button>
              </div>
            )}
          </div>
        
        <div className="detail-content">
          <div className="detail-image-section">
            <div className="image-title-section">
              <h2 className="detail-title">{currentImage.caption}</h2>
              {formatHijriDate(currentImage.createdAt) && (
                <p style={{
                  margin: '6px 0 0 0',
                  fontSize: '0.85rem',
                  color: '#bbb',
                  direction: 'rtl'
                }}>
                  أنشئ بتاريخ {formatHijriDate(currentImage.createdAt)}
                </p>
              )}
            </div>
            <img 
              src={image.url} 
              alt={image.caption} 
              className="detail-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/tweet-icon.jpg';
                e.target.style.objectFit = 'contain';
              }}
            />
          </div>
          
          <div className="detail-text-section">
            {currentImage.extractedText ? (
              <div className="extracted-text">
                <h3>📝 التفريغ النصي:</h3>
                <div className="text-content">
                  {currentImage.extractedText}
                </div>
              </div>
            ) : (
              <div className="no-text">
                <p>⏳ التفريغ النصي قيد الإعداد...</p>
              </div>
            )}

            <div className="category-display-section">
              <div className="current-category">
                <span className="category-label">التصنيف:</span>
                <span className="category-badge">{currentImage.category || 'متنوعة'}</span>
              </div>
            </div>

            <div className="share-buttons">
              <h4>للمشاركة:</h4>
              <div className="share-buttons-group">
                <button className="share-btn copy" onClick={copyText} title="نسخ النص">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </button>
                <button className="share-btn download" onClick={downloadImage} title="حفظ التصميم">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                </button>
                <button className="share-btn whatsapp" onClick={shareOnWhatsApp} title="مشاركة على واتساب">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
                <button className="share-btn twitter" onClick={shareOnTwitter} title="مشاركة على تويتر">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </button>
                <button className="share-btn copylink" onClick={copyLink} title="نسخ رابط الصفحة">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 7h-4v2h4c1.65 0 3 1.35 3 3s-1.35 3-3 3h-4v2h4c2.76 0 5-2.24 5-5s-2.24-5-5-5zm-6 8H7c-1.65 0-3-1.35-3-3s1.35-3 3-3h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-2zm-3-4h8v2H8z"/>
                  </svg>
                </button>
                <button className="share-btn telegram" onClick={shareOnTelegram} title="مشاركة على تلجرام">
                  <svg className="share-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="detail-footer">
          <p>© {getHijriYear()}هـ سلمان بن عبدالله الحران | جميع الحقوق محفوظة</p>
        </div>
      </div>
    </div>
    </>
  );
}

export default ImageDetail;
